import { createClient } from "@supabase/supabase-js";
import { Database } from "./types/database";

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL || import.meta.env.NEXT_PUBLIC_SUPABASE_URL || "https://cocfqstuqqzynckoymuw.supabase.co"
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNvY2Zxc3R1cXF6eW5ja295bXV3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM3MDgzMDgsImV4cCI6MjA2OTI4NDMwOH0.-TIzp2K_BM1-VNYhMm_YgYVDNut4wfpSxP7DFB-485M"

// Add validation for environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn("⚠️ Supabase environment variables are not configured properly");
  console.warn(
    "VITE_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_URL:",
    supabaseUrl ? "Set" : "Missing"
  );
  console.warn(
    "NEXT_PUBLIC_SUPABASE_ANON_KEY:",
    supabaseAnonKey ? "Set" : "Missing"
  );
}

// Create a mock supabase client if environment variables are missing
const mockSupabase = {
  auth: {
    getSession: async () => ({ data: { session: null }, error: null }),
    getUser: async () => ({ data: { user: null }, error: null }),
    signIn: async () => ({ data: { user: null }, error: null }),
    signOut: async () => ({ error: null }),
  },
  from: () => ({
    select: () => ({ eq: () => ({ single: async () => ({ data: null, error: null }) }) }),
    insert: async () => ({ error: null }),
  }),
  channel: () => ({ subscribe: () => ({ unsubscribe: () => {} }) }),
  removeChannel: () => {},
};

export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient<Database>(supabaseUrl, supabaseAnonKey)
  : mockSupabase as any;

// Helper functions for common operations
export const supabaseHelpers = {
  // Get current user with role
  async getCurrentUser() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: userData } = await supabase
        .from("users")
        .select("*")
        .eq("id", user.id)
        .single();

      return userData;
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  },

  // Get agent profile for current user
  async getCurrentAgent() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: agent } = await supabase
        .from("agents")
        .select("*, user:users(*)")
        .eq("user_id", user.id)
        .single();

      return agent;
    } catch (error) {
      console.error("Error getting current agent:", error);
      return null;
    }
  },

  // Check if user has permission for action
  async hasPermission(action: string, resourceId?: string) {
    try {
      const user = await this.getCurrentUser();
      if (!user) return false;

      // Super admin has all permissions
      if (user.role === "super_admin") return true;

      // Add specific permission logic here
      return false;
    } catch (error) {
      console.error("Error checking permissions:", error);
      return false;
    }
  },

  // Log activity
  async logActivity({
    activity_type,
    description,
    entity_type,
    entity_id,
    metadata,
  }: {
    activity_type: string;
    description: string;
    entity_type?: string;
    entity_id?: string;
    metadata?: any;
  }) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      return supabase.from("activity_logs").insert({
        user_id: user?.id,
        activity_type: activity_type as any,
        description,
        entity_type,
        entity_id,
        metadata,
      });
    } catch (error) {
      console.error("Error logging activity:", error);
      return { error };
    }
  },

  // Send notification
  async sendNotification({
    recipient_id,
    title,
    message,
    related_type,
    related_id,
    action_url,
  }: {
    recipient_id: string;
    title: string;
    message: string;
    related_type?: string;
    related_id?: string;
    action_url?: string;
  }) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      return supabase.from("notifications").insert({
        recipient_id,
        sender_id: user?.id,
        title,
        message,
        related_type,
        related_id,
        action_url,
      });
    } catch (error) {
      console.error("Error sending notification:", error);
      return { error };
    }
  },
};
